






























import { Component, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import RevenueReportInjectorMixin from "@/mixins/revenueReportInjectorMixin";

// Types
import { IReportProductCategoryRow } from "@/models/reports";

@Component
export default class RevenueReport extends Mixins(RevenueReportInjectorMixin) {
  // Rendering components

  public get tableHeader() {
    return {
      render: (h: CreateElement) =>
        h("thead", {}, [
          h(
            "tr",
            {},
            _.concat(
              h("th", {}, this.showSkeleton ? this.createSkeletonCell(h) : []),
              this.createMonthsCells(h)
            )
          )
        ])
    };
  }

  public get invoicesBody() {
    return {
      render: (h: CreateElement) =>
        h(
          "tbody",
          {},
          this.createDataRows({
            h,
            title: this.$t("_.reports.invoices_raised").toString(),
            dataCategories: this.revenueReportData.invoicesCategories,
            totalLabel: this.$t("_.reports.total_raised").toString(),
            totalKey: "all_invoices_raised_formatted"
          })
        )
    };
  }

  public get cashInBody() {
    return {
      render: (h: CreateElement) => h("tbody", {}, this.createCashInRows(h))
    };
  }

  public get accountsBody() {
    return {
      render: (h: CreateElement) =>
        h(
          "tbody",
          {},
          this.createDataRows({
            h,
            title: this.$t("_.reports.accounts_receivable").toString(),
            dataCategories: this.revenueReportData.accountsCategories,
            totalLabel: this.$t("_.reports.accounts_receivable_end").toString(),
            totalKey: "unpaid_at_end_of_period_formatted"
          })
        )
    };
  }

  public get importedBalancesBody() {
    return {
      render: (h: CreateElement) =>
        h(
          "tbody",
          {},
          this.createDataRows({
            h,
            title: this.$t("_.reports.imported_balances").toString(),
            dataCategories: this.revenueReportData.importedBalancesCategories
          })
        )
    };
  }

  public get revenueBody() {
    return {
      render: (h: CreateElement) =>
        h(
          "tbody",
          {},
          this.createDataRows({
            h,
            title: this.$t("_.reports.revenue_recognition").toString(),
            dataCategories: this.revenueReportData.revenueCategories,
            totalLabel: this.$t("_.reports.recognised_revenue").toString(),
            totalKey: "total_monthly_revenue_formatted"
          })
        )
    };
  }

  public get clientsBody() {
    return {
      render: (h: CreateElement) =>
        h(
          "tbody",
          {},
          this.createDataRows({
            h,
            title: this.$t("_.clients").toString(),
            dataCategories: this.revenueReportData.clientsCategories,
            totalLabel: this.$t("_.reports.annual_churn").toString(),
            totalKey: "annual_churn_rate"
          })
        )
    };
  }

  public get ltvBody() {
    return {
      render: (h: CreateElement) =>
        h(
          "tbody",
          {},
          this.createDataRows({
            h,
            title: this.$t("_.reports.ltv").toString(),
            dataCategories: this.revenueReportData.ltvCategories,
            totalLabel: this.$t("_.reports.avg_ltv").toString(),
            totalKey: "monthly_ltv_formatted"
          })
        )
    };
  }

  public get revenueByProductCategoryBody() {
    if (_.isEmpty(this.revenueReportData.productCategories)) return "";

    return {
      render: (h: CreateElement) =>
        h(
          "tbody",
          {},
          this.createRevenueByCategoryRows(
            h,
            this.$t("_.reports.revenue_by_product_category").toString(),
            this.revenueReportData.productCategories,
            "total_monthly_revenue_categories_sum_formatted"
          )
        )
    };
  }

  public get revenueByProductOptionBody() {
    if (_.isEmpty(this.revenueReportData.productOptions)) return "";

    return {
      render: (h: CreateElement) =>
        h(
          "tbody",
          {},
          this.createRevenueByCategoryRows(
            h,
            this.$t("_.reports.revenue_by_product_option").toString(),
            this.revenueReportData.productOptions,
            "total_monthly_revenue_options_sum_formatted"
          )
        )
    };
  }

  // Methods

  public createMonthsCells(h: CreateElement) {
    return _.map(this.reportColumns, columnKey => {
      return h(
        "th",
        {},
        this.showSkeleton
          ? this.createSkeletonCell(h)
          : this.$moment
              .utc(columnKey)
              .local()
              .format(this.$t("_datetime.month.middle") as string)
      );
    });
  }

  public createDataRows({
    h,
    title,
    dataCategories,
    totalLabel = "",
    totalKey = ""
  }) {
    return _.concat(
      [
        h("tr", {}, [
          h("th", {}, this.showSkeleton ? this.createSkeletonCell(h) : title),
          h(
            "th",
            {
              attrs: { colspan: this.reportColumns.length }
            },
            []
          )
        ])
      ],
      _.map(dataCategories, category => {
        return h("tr", {}, [
          h(
            "th",
            {},
            this.showSkeleton ? this.createSkeletonCell(h) : category.label
          ),
          _.map(this.reportColumns, columnLabel => {
            return h(
              "td",
              {},
              this.findCellValue(columnLabel, category.valueKey, h)
            );
          })
        ]);
      }),
      totalLabel
        ? [
            h("tr", {}, [
              h(
                "th",
                {},
                this.showSkeleton ? this.createSkeletonCell(h) : totalLabel
              ),
              _.map(this.reportColumns, columnLabel => {
                return h(
                  "td",
                  {},
                  this.findCellValue(columnLabel, totalKey, h)
                );
              })
            ])
          ]
        : []
    );
  }

  public createCashInRows(h: CreateElement) {
    return _.concat(
      [
        h("tr", {}, [
          h(
            "th",
            {},
            this.showSkeleton
              ? this.createSkeletonCell(h)
              : this.$t("_.reports.cash_in").toString()
          ),
          h(
            "th",
            {
              attrs: { colspan: this.reportColumns.length }
            },
            []
          )
        ])
      ],
      _.map(this.revenueReportData.cashInCategories, category => {
        return h(
          "tr",
          {},
          _.concat(
            [
              h(
                "th",
                {},
                this.showSkeleton ? this.createSkeletonCell(h) : category.label
              )
            ],
            _.map(category.columns, column => {
              return h(
                "td",
                {},
                this.showSkeleton
                  ? this.createSkeletonCell(h)
                  : this.createCurrencyCell(
                      h,
                      column,
                      category.currencyCode ===
                        this.revenueReportData.reportCurrency
                    )
              );
            })
          )
        );
      }),
      [
        h("tr", {}, [
          h(
            "th",
            {},
            this.showSkeleton
              ? this.createSkeletonCell(h)
              : this.$t("_.reports.total_cash_in").toString()
          ),
          _.map(this.reportColumns, columnLabel => {
            return h(
              "td",
              {},
              this.findCellValue(
                columnLabel,
                "cash_received_in_month_formatted",
                h
              )
            );
          })
        ])
      ]
    );
  }

  public createRevenueByCategoryRows(
    h: CreateElement,
    title,
    itemsCategories: IReportProductCategoryRow[],
    totalKey
  ) {
    return _.concat(
      [
        h("tr", {}, [
          h("th", {}, this.showSkeleton ? this.createSkeletonCell(h) : title),
          h(
            "th",
            {
              attrs: { colspan: this.reportColumns.length }
            },
            []
          )
        ])
      ],
      _.map(itemsCategories, (category, index) => {
        return h(
          "tr",
          index !== itemsCategories.length - 1
            ? {}
            : { class: { "u-last-row": true } },
          _.concat(
            [
              h(
                "th",
                {},
                this.showSkeleton ? this.createSkeletonCell(h) : category.label
              )
            ],
            _.map(category.columns, ({ amount, amountFormatted }) => {
              if (this.showSkeleton)
                return h("td", {}, this.createSkeletonCell(h));
              return h(
                "td",
                {
                  on: {
                    click: () =>
                      this.$copyToClipboard((amount ?? "–").toString())
                  }
                },
                amountFormatted
              );
            })
          )
        );
      }),
      [
        h("tr", {}, [
          h(
            "th",
            {},
            this.showSkeleton
              ? this.createSkeletonCell(h)
              : this.$t("_.reports.total_monthly_revenue").toString()
          ),
          _.map(this.reportColumns, columnLabel => {
            return h("td", {}, this.findCellValue(columnLabel, totalKey, h));
          })
        ])
      ]
    );
  }

  public createSkeletonCell(h: CreateElement) {
    return [
      h(
        "b-skeleton",
        { props: { animated: this.isLoading }, attrs: { width: "100%" } },
        []
      )
    ];
  }

  public createCurrencyCell(
    h: CreateElement,
    { amount, amountFormatted, amountConvertedFormatted },
    showDefaultCurrency
  ) {
    if (typeof amountFormatted === "undefined") return "–";
    if (!showDefaultCurrency) {
      return [
        h(
          "u-abbr",
          {
            nativeOn: { click: () => this.$copyToClipboard(amount.toString()) },
            props: {
              tooltip: `${amountConvertedFormatted}`,
              multilined: false
            }
          },
          amountFormatted
        )
      ];
    }
    return amountFormatted;
  }

  public findCellValue(columnLabel, rowLabel, h) {
    return this.showSkeleton
      ? this.createSkeletonCell(h)
      : _.get(
          _.find(this.revenueReport?.months, (value, key) => {
            return columnLabel === key;
          }),
          rowLabel
        );
  }

  // Computed

  public get isLoading() {
    return (
      this.revenueReportData.isLoading || this.revenueReportData.isProcessing
    );
  }

  public get reportColumns() {
    return _.isEmpty(this.revenueReport)
      ? Array(12)
      : _.keys(this.revenueReport?.months);
  }

  public get showEmptyState() {
    return !this.isLoading && _.isEmpty(this.revenueReport);
  }

  public get showSkeleton() {
    return this.isLoading || (!this.isLoading && _.isEmpty(this.revenueReport));
  }
}
